import styled from 'styled-components';
import { useRef } from "react";
import emailjs from "@emailjs/browser";
import { Snackbar } from "@mui/material";
import React, { Suspense, lazy } from "react";
import { TypographyH2, TypographyLead } from "../ui/Typography";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  margin-top: 2rem;
  align-items: center;
  @media (max-width: 960px) {
    padding: 0px;
  }
`;

const EarthyCanvas = lazy(() => import('../canvas/Earth'));

// New container to handle flex layout for desktop view
const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 0.5rem;
  max-width: 1350px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const EarthStyles = styled.div`
display: flex;
width: 100%;
height:30rem;
order: 1; /* Default order for desktop view */
  @media (max-width: 960px) {
    order: -1; /* Change order for mobile view */
    height: 10rem;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0px 25px 30px 25px;
  gap: 12px;
  box-sizing: border-box;
`;

const Title = styled(TypographyH2)`
  font-size: 42px;
  text-align: center;
  margin-top: 20px;
  border-bottom: none;
  padding-top: 2rem;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;

const Desc = styled(TypographyLead)`
  font-size: 16px;
  text-align: center;
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const ContactForm = styled.form`
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(3, 7, 18, 0.95) 0%, rgba(3, 7, 18) 100%);
  padding: 32px;
  border-radius: 16px;
  border: 1.5px solid rgba(59, 130, 246, 0.36);
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.36);
  margin-top: 28px;
  gap: 12px;
  box-sizing: border-box;
`;

const ContactTitle = styled.div`
  font-size: 24px;
  margin-bottom: 6px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`;

const ContactInput = styled.input`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary + 90};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid #0551ff;
  }
`;

const ContactInputMessage = styled.textarea`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary + 90};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  resize: none;
  &:focus {
    border: 1px solid #0551ff;
  }
`;



const ContactButton = styled.input`
  padding: 13px 16px;
  font-weight: 600;
  height: 50px;
  align-items: center;
  background-image: linear-gradient(144deg, #1e3a8a, #3b82f6 50%, #00ddeb);
  border: 0;
  border-radius: 8px;
  box-shadow: rgba(59, 130, 246, 0.2) 0 15px 30px -5px;
  box-sizing: border-box;
  color: #ffffff;
  display: flex;
  font-family: Phantomsans, sans-serif;
  font-size: 18px;
  justify-content: center;
  line-height: 1em;
  max-width: 100%;
  min-width: 140px;
  padding: 3px;
  text-decoration: none;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s;
  outline: 0;

  &:active,
  &:hover {
    outline: 0;
  }

  span {
    background-color: rgb(5, 6, 45);
    padding: 16px 24px;
    border-radius: 6px;
    width: 100%;
    height: 100%;
    transition: 300ms;
  }

  &:hover span {
    background: none;
  }

  &:active {
    transform: scale(0.9);
  }
`;

const Contact = () => {
  // hooks
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const form = useRef();

  const isEmailValid = (email) => {
    // Simple email validation regex
    const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData(form.current);
    const user_email = formData.get("user_email");
    const user_name = formData.get("user_name");
    const subject = formData.get("subject");
    const message = formData.get("message");

    if (!user_email || !user_name || !subject || !message) {
      setErrorMsg("Please fill in all fields.");
      setError(true);
      return;
    }

    if (!isEmailValid(user_email)) {
      setErrorMsg("Please enter a valid email address.");
      setError(true);
      return;
    }

    emailjs
      .sendForm(
        "service_w7dzhh5",
        "template_b0q19al",
        form.current,
        "LXKUbff1C0vzu7wIX"
      )
      .then(
        (result) => {
          setOpen(true);
          form.current.reset();
        },
        (error) => {
          console.log(error.text);
        }
      );
  };

  return (
    <Container id="contact">
      <Title>Contact</Title>
          <Desc>
            Feel free to reach out to me for any questions or opportunities!
          </Desc>
      <FlexContainer>

        <Wrapper>
          
          <ContactForm ref={form} onSubmit={handleSubmit}>
            <ContactTitle>Email Me </ContactTitle>
            <ContactInput placeholder="Your Email" name="user_email" />
            <ContactInput placeholder="Your Name" name="user_name" />
            <ContactInput placeholder="Subject" name="subject" />
            <ContactInputMessage placeholder="Message" rows="4" name="message" />
            <ContactButton type="submit" value="Send Message" />
          </ContactForm>
          <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            message="Email sent successfully!"
            severity="success"
          />
          <Snackbar
            open={error}
            autoHideDuration={6000}
            onClose={() => setError(false)}
            message={errorMsg}
            severity="error"
          />
        </Wrapper>
        <EarthStyles>
          <Suspense fallback={<div>Loading...</div>}>
            <EarthyCanvas />
          </Suspense>
        </EarthStyles>

      </FlexContainer>
    </Container>
  );
};

export default Contact;
